import { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';

import { filledInputClasses, InputAdornment, useControlled, useForkRef } from '@mui/material';

import { useTextInputValueOverflow } from '../../utils';
import {
  getNumberOfLettersLabel,
  TextInputClearIcon,
  TextInputEndAdornmentIcon,
} from '../TextInput';
import { Tooltip } from '../Tooltip';
import { textInputGhostRootClasses } from './theme';

import { StyledTextInputGhost } from './TextInputGhost.styled';

import type { ChangeEventHandler, FocusEventHandler, MouseEventHandler } from 'react';
import type { TextInputGhostOwnerState, TextInputGhostProps } from './types';

const TextInputGhost = (props: TextInputGhostProps) => {
  const {
    startAdornment,
    endAdornment,
    endAction,
    loading,
    error,
    success,
    onClear,
    disabledClearable,
    value: valueProps,
    defaultValue: defaultValueProps,
    onFocus,
    onBlur,
    onChange,
    size = 'sm',
    disabledOverflow,
    slotProps,
    displayNumberOfLetters: displayNumberOfLettersProps,
    multiline,
    readOnly,
    disabled,
    resizable = false,
    inputRef,
    ref,
    autoFocus,
    ...others
  } = props;
  const maxLength = slotProps?.input?.maxLength;

  const [value, setValueState] = useControlled({
    controlled: valueProps,
    default: defaultValueProps,
    name: 'TextInputGhost',
    state: 'value',
  });

  const numberOfLettersLabel = getNumberOfLettersLabel(value, maxLength);
  const displayNumberOfLetters =
    multiline &&
    displayNumberOfLettersProps &&
    !readOnly &&
    !disabled &&
    numberOfLettersLabel.length > 0;

  // overflow 기능과 외부 ref를 모두 처리하는 containerRef
  const { showTooltip, handleShowTooltip, containerRef } =
    useTextInputValueOverflow<HTMLDivElement>({
      disabledOverflow,
    });

  const forkedRef = useForkRef(inputRef, containerRef);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const displayClearIcon = isFocused && !readOnly;

  const displayEllipsisTooltip = useMemo(() => {
    if (!value) return null;

    if (isFocused) return null;

    if (!showTooltip) return null;

    if (disabledOverflow) return null;

    return <>{value as string}</>;
  }, [disabledOverflow, isFocused, showTooltip, value]);

  const handleFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    event => {
      if (onFocus) {
        onFocus(event);
      }
      setIsFocused(true);
    },
    [onFocus],
  );

  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    event => {
      // MUI InputBase는 disabled 상태 변경 시 event 없이 onBlur()를 호출할 수 있음
      // @see https://github.com/mui/material-ui/blob/master/packages/mui-material/src/InputBase/InputBase.js#L328-L335
      if (onBlur && event) {
        onBlur(event);
      }
      setIsFocused(false);
      handleShowTooltip();

      // 멀티라인일 때 스크롤을 맨 위로 이동
      if (multiline && event?.target) {
        event.target.scrollTop = 0;
      }
    },
    [handleShowTooltip, multiline, onBlur],
  );

  const handleClear: MouseEventHandler<SVGGElement> = useCallback(
    event => {
      if (event.nativeEvent.defaultPrevented) {
        return;
      }

      setValueState('');

      if (onClear) {
        onClear(event);
      }
    },
    [onClear, setValueState],
  );

  const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    event => {
      if (event.nativeEvent.defaultPrevented) {
        return;
      }

      let newValue = event.target.value;

      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength);
      }

      setValueState(newValue);

      if (onChange) {
        onChange(event, newValue);
      }
    },
    [maxLength, onChange, setValueState],
  );

  const statusDisabled = !!disabled;
  const displayError = !statusDisabled && error;
  const displaySuccess = !statusDisabled && success;

  // ownerState 구성
  const ownerState: TextInputGhostOwnerState = {
    ...props,
    size,
    resizable,
    error: displayError,
    success: displaySuccess,
  };

  return (
    <Tooltip message={displayEllipsisTooltip} placement="top" arrow>
      <StyledTextInputGhost
        {...others}
        autoFocus={autoFocus}
        multiline={multiline}
        ref={ref}
        inputRef={forkedRef}
        ownerState={ownerState}
        readOnly={readOnly}
        disabled={disabled}
        slotProps={{
          root: {
            ...slotProps?.root,
            className: clsx([
              filledInputClasses.root,
              loading && textInputGhostRootClasses.loading,
              displaySuccess && textInputGhostRootClasses.success,
            ]),
          },
          input: {
            ...slotProps?.input,
            ...(autoFocus && { 'data-autofocus': true }),
            className: clsx([
              filledInputClasses.input,
              !disabledOverflow && textInputGhostRootClasses.overflow,
              slotProps?.input?.className,
            ]),
          },
        }}
        size={size}
        value={value}
        error={displayError}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        startAdornment={
          startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            {typeof endAdornment === 'string' ? <span>{endAdornment}</span> : endAdornment}
            {displayNumberOfLetters ? <span>{numberOfLettersLabel}</span> : null}
            <TextInputEndAdornmentIcon
              loading={loading}
              error={displayError}
              success={displaySuccess}
              size={size}
            />
            {displayClearIcon && (
              <TextInputClearIcon
                onClear={handleClear}
                disabledClearable={disabledClearable}
                value={value}
              />
            )}
            {endAction ?? null}
          </InputAdornment>
        }
      />
    </Tooltip>
  );
};

TextInputGhost.displayName = 'TextInputGhost';

export default TextInputGhost;
